import React, { useState } from 'react'
import { CanvasProvider, Flex, FormField, Menu, Modal, PrimaryButton, space, TertiaryButton, Text, TextInput, Tooltip, useMenuModel, useModalModel } from '@workday/canvas-kit-react'
import { relatedActionsVerticalIcon } from '@workday/canvas-system-icons-web'
import { RespondAppConfiguration } from '../models/RespondAppConfiguration'
import { MenuTheme, PrimaryButtonColors } from '../../components/CanvasThemes'
import { PanelSide } from '../RespondAppCore'

export interface RespondRelatedActionsProps {
  appConfiguration: RespondAppConfiguration
  showSave: boolean
  defaultName: string
  panelSide: PanelSide
  onCreateFeed: () => void
  onConfigureFeed: () => void
  onViewSavedFeeds: () => void
  onViewDisplaySettings: () => void
  onSaveFeed: (name: string) => void
  onHeaderCollapse: () => void
  onTogglePanelSide: () => void
}

const RespondRelatedActions = ({
  appConfiguration,
  showSave,
  defaultName,
  panelSide,
  onCreateFeed,
  onConfigureFeed,
  onViewSavedFeeds,
  onViewDisplaySettings,
  onSaveFeed,
  onHeaderCollapse,
  onTogglePanelSide,
}: RespondRelatedActionsProps) => {
  const [name, setName] = useState('')
  const model = useModalModel()

  const menuModel = useMenuModel({
    onShow: () => {},
    onSelect: data => {
      switch (data.id) {
        case 'create-new-feed':
          onCreateFeed()
          break
        case 'view-saved-feeds':
          onViewSavedFeeds()
          break
        case 'display-settings':
          onViewDisplaySettings()
          break
        case 'feed-settings':
          onConfigureFeed()
          break
        case 'header-collapse':
          onHeaderCollapse()
          break
        case 'panel-switch':
          onTogglePanelSide()
          break
      }
    },
    initialSelectedIds: []
  })
  return (
    <CanvasProvider theme={MenuTheme}>
      <Modal model={model}>
        <Menu model={menuModel}>
          <Tooltip title="More Menu">
            <Menu.Target as={TertiaryButton} icon={relatedActionsVerticalIcon} />
          </Tooltip>
          <Menu.Popper>
            <Menu.Card>
              <Menu.List>
                {appConfiguration.context === 'PUBLIC' && (
                  <>
                    <Menu.Item data-id="feed-settings">
                      <Menu.Item.Text>Edit Feed</Menu.Item.Text>
                    </Menu.Item>
                    {showSave && (
                      <Modal.Target as={Text}>
                        <Menu.Item data-id="save-feed">
                          <Menu.Item.Text>Save Feed</Menu.Item.Text>
                        </Menu.Item>
                      </Modal.Target>
                    )}
                    <Menu.Divider />
                  </>
                )}
                {appConfiguration.context === 'PUBLIC' && !appConfiguration.isEmbedded && (
                  <>
                    <Menu.Item data-id="create-new-feed">
                      <Menu.Item.Text>Create New Feed</Menu.Item.Text>
                    </Menu.Item>
                    <Menu.Item data-id="view-saved-feeds">
                      <Menu.Item.Text>View All Feeds</Menu.Item.Text>
                    </Menu.Item>
                    <Menu.Divider />
                  </>
                )}
                <Menu.Item data-id="display-settings">
                  <Menu.Item.Text>Change Time Format</Menu.Item.Text>
                </Menu.Item>
                <Menu.Item data-id="panel-switch">
                  <Menu.Item.Text>
                    Move List to {panelSide === 'right' ? "Left" : "Right"}
                  </Menu.Item.Text>
                </Menu.Item>
                <Menu.Item data-id="header-collapse">
                  <Menu.Item.Text>Hide Header</Menu.Item.Text>
                </Menu.Item>
              </Menu.List>
            </Menu.Card>
          </Menu.Popper>
        </Menu>
        <Modal.Overlay>
          <Modal.Card>
            <Modal.Body>
              <FormField label="Feed Name">
                <TextInput
                  placeholder={defaultName}
                  value={name}
                  onChange={event => setName(event.currentTarget.value)}
                />
              </FormField>
              <Flex flexDirection="row" gap={space.xs}>
                <PrimaryButton
                  onClick={event => {
                    onSaveFeed(name === '' ? defaultName : name)
                    model.events.hide()
                  }}
                  colors={PrimaryButtonColors}
                >
                  Save
                </PrimaryButton>
                <Modal.CloseButton>Cancel</Modal.CloseButton>
              </Flex>
            </Modal.Body>
          </Modal.Card>
        </Modal.Overlay>
      </Modal>
    </CanvasProvider>
  )
}

export default RespondRelatedActions
